import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import CtaFinal from "@/components/CtaFinal";
import { Reveal, MaskedLineInView, Eyebrow } from "@/components/Reveal";
import { IMG } from "@/data/content";

const Impresa = () => (
  <>
    <SEO
      title="L'Impresa — SOPAJ | Impresa Edile a Nibionno"
      description="Sopaj è un'impresa edile con sede a Nibionno, specializzata in ristrutturazioni, opere edili e gestione completa del cantiere. Un unico punto di riferimento per tutte le fasi del lavoro, anche al di fuori della zona di sede."
      path="/impresa"
    />

    <section data-testid="impresa-hero" className="max-w-7xl mx-auto px-5 md:px-8 pt-36 md:pt-48 pb-16 md:pb-24">
      <Reveal><Eyebrow number="01" label="L'impresa" /></Reveal>
      <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-anthracite max-w-3xl">
        <MaskedLineInView>Esperienza costruita</MaskedLineInView>
        <MaskedLineInView delay={0.1}>
          <span className="font-serif italic font-normal">sul campo.</span>
        </MaskedLineInView>
      </h1>
    </section>

    <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24 md:pb-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7 space-y-6 text-anthracite/75 leading-relaxed text-base md:text-lg">
          <Reveal>
            <p>
              Sopaj è un'impresa edile con sede a Nibionno specializzata in
              ristrutturazioni, opere edili e gestione completa del cantiere.
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <p>
              L'esperienza maturata sul campo permette di seguire interventi di diversa
              tipologia, dalle singole lavorazioni fino alle ristrutturazioni complete
              chiavi in mano.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <p>
              Ogni progetto viene valutato in modo concreto, con l'obiettivo di offrire
              al cliente un unico punto di riferimento durante tutte le fasi del lavoro.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="border-l-2 border-terracotta pl-6 font-serif italic text-xl text-anthracite">
              Un unico referente. Tutto il cantiere.
            </p>
          </Reveal>
          <Reveal delay={0.32}>
            <p>
              La sede dell'impresa è a Nibionno, ma gli interventi vengono valutati
              senza una rigida limitazione territoriale, in funzione delle
              caratteristiche e delle esigenze del progetto.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -top-5 -right-5 w-full h-full border border-terracotta/40" aria-hidden="true" />
              <img
                src={IMG.architettura}
                alt="Architettura contemporanea — Sopaj impresa edile Nibionno"
                className="relative w-full aspect-[4/5] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section data-testid="impresa-valori" className="bg-sand/60">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <Reveal><Eyebrow number="02" label="Come lavoriamo" /></Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-anthracite/10">
          {[
            { n: "01", t: "Rapporto diretto", d: "Parli con chi segue il cantiere, non con un call center." },
            { n: "02", t: "Gestione completa", d: "Coordiniamo noi tutte le lavorazioni, in ogni fase del cantiere." },
            { n: "03", t: "Preventivi chiari", d: "Ogni lavorazione è descritta in modo trasparente prima di iniziare." },
            { n: "04", t: "Disponibilità", d: "La sede è a Nibionno: ogni progetto viene valutato in base alle sue caratteristiche, anche al di fuori della zona locale." },
          ].map((v, i) => (
            <Reveal key={v.n} delay={i * 0.08}>
              <div data-testid={`valore-${v.n}`} className="border-b border-r border-anthracite/10 p-8 h-full bg-panna/40">
                <p className="font-display text-terracotta text-sm tracking-[0.2em]">{v.n}</p>
                <h2 className="mt-4 font-display text-xl tracking-tight text-anthracite">{v.t}</h2>
                <p className="mt-3 text-sm text-concrete leading-relaxed">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link
            to="/chiavi-in-mano"
            data-testid="impresa-process-cta"
            className="group mt-12 inline-flex items-center gap-3 font-display text-sm tracking-[0.15em] uppercase text-terracotta"
          >
            <span className="link-underline">Scopri come seguiamo una ristrutturazione</span>
            <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>

    <CtaFinal />
  </>
);

export default Impresa;
